import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { jwtDecode } from 'jwt-decode'

/**
 * User information extracted from ID token
 */
export interface User {
  sub: string // User ID
  email: string
  name?: string
  groups?: string[]
  [key: string]: unknown
}

/**
 * JWT token claims
 */
interface TokenClaims {
  sub: string
  email: string
  name?: string
  'cognito:groups'?: string[]
  exp: number
  iat: number
  [key: string]: unknown
}

/**
 * Auth Store
 * 
 * Manages authentication state, tokens, and user information.
 * Provides getters for token expiration checks and user display information.
 */
export const useAuthStore = defineStore('auth', () => {
  // ============================================================================
  // State
  // ============================================================================

  const isAuthenticated = ref(false)
  const user = ref<User | null>(null)
  const accessToken = ref<string | null>(null)
  const refreshToken = ref<string | null>(null)
  const tokenExpiry = ref<number | null>(null) // Unix timestamp in seconds

  // ============================================================================
  // Getters
  // ============================================================================

  /**
   * Check if the access token is expired
   */
  const isTokenExpired = computed((): boolean => {
    if (!tokenExpiry.value) return true
    
    const now = Math.floor(Date.now() / 1000) // Current time in seconds
    return now >= tokenExpiry.value
  })

  /**
   * Check if the access token is expiring soon (within 5 minutes)
   */
  const isTokenExpiringSoon = computed((): boolean => {
    if (!tokenExpiry.value) return true
    
    const now = Math.floor(Date.now() / 1000)
    const fiveMinutes = 5 * 60 // 5 minutes in seconds
    return (tokenExpiry.value - now) <= fiveMinutes
  })

  /**
   * Get user display name (name or email)
   */
  const userDisplayName = computed((): string => {
    if (!user.value) return ''
    return user.value.name || user.value.email || 'User'
  })

  /**
   * Get user groups for role-based access control
   */
  const userGroups = computed((): string[] => {
    if (!user.value || !user.value.groups) return []
    return user.value.groups
  })

  // ============================================================================
  // Actions
  // ============================================================================

  /**
   * Store authentication tokens and extract user information
   * 
   * @param tokens - Object containing access, refresh, and ID tokens
   */
  function setTokens(tokens: {
    accessToken: string
    refreshToken: string
    idToken: string
  }): void {
    try {
      // Store tokens
      accessToken.value = tokens.accessToken
      refreshToken.value = tokens.refreshToken

      // Decode access token to get expiry
      const accessClaims = jwtDecode<TokenClaims>(tokens.accessToken)
      tokenExpiry.value = accessClaims.exp

      // Decode ID token to get user info
      const idClaims = jwtDecode<TokenClaims>(tokens.idToken)
      
      // Extract user information
      user.value = {
        sub: idClaims.sub,
        email: idClaims.email,
        name: idClaims.name,
        groups: idClaims['cognito:groups'] || [],
      }

      isAuthenticated.value = true
    } catch (error) {
      console.error('Error decoding tokens:', error)
      clearAuth()
      throw new Error('Invalid token format')
    }
  }

  /**
   * Update user information
   * 
   * @param userInfo - User information object
   */
  function setUser(userInfo: User): void {
    user.value = userInfo
  }

  /**
   * Clear all authentication state
   */
  function clearAuth(): void {
    isAuthenticated.value = false
    user.value = null
    accessToken.value = null
    refreshToken.value = null
    tokenExpiry.value = null
  }

  /**
   * Check authentication status and validate token
   * 
   * @returns true if authenticated and token is valid, false otherwise
   */
  function checkAuthStatus(): boolean {
    if (!isAuthenticated.value || !accessToken.value) {
      return false
    }

    // Check if token is expired
    if (isTokenExpired.value) {
      return false
    }

    return true
  }

  // ============================================================================
  // Return
  // ============================================================================

  return {
    // State
    isAuthenticated,
    user,
    accessToken,
    refreshToken,
    tokenExpiry,

    // Getters
    isTokenExpired,
    isTokenExpiringSoon,
    userDisplayName,
    userGroups,

    // Actions
    setTokens,
    setUser,
    clearAuth,
    checkAuthStatus,
  }
})
